import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Announcement } from '../announcements/announcement.model';
import { AnnouncementService } from '../announcements/announcement.service';
import { ReleaseVersionService } from '../footer/release-version.service';
import { isChunkLoadError } from './is-chunk-load-error';
import { pageLoader, reloadOnce } from './reload-once';

const CHECK_INTERVAL_MILLISECONDS = 5 * 60 * 1000;
const CHECK_AFTER_NAVIGATION_MILLISECONDS = 60 * 1000;

/**
 * Moves a tab running an older release onto the deployed one, with a full page load of the page
 * the user asked for.
 *
 * Two ways of learning that the tab is stale:
 *
 * - A navigation fails because its lazily loaded code file is gone (deleted by the deployment).
 *   The page asked for is loaded in full at once. This works in any build.
 * - The release check: the running release (the `release` tag the deployment workflow writes
 *   into `index.html`, read by ReleaseVersionService) differs from the hash in `version.json`,
 *   fetched when the tab becomes visible again, every 5 minutes while it is visible, and after a
 *   navigation if the last check was over a minute ago. The next navigation then loads the page
 *   asked for in full, and the new release is announced in the announcement banner, with a button that loads it. A local build
 *   has no tag, and is not checked; a failed fetch counts as no news.
 *
 * Drafts survive the full page load because forms save them as they change.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseCheckService {
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private announcementService = inject(AnnouncementService);
  private readonly runningRelease = inject(ReleaseVersionService).frontend.hash;

  private lastCheckAt = 0;
  private newReleaseDeployed = false;
  private announcedId: string | null = null;

  start(): void {
    const navigation = this.router.events.subscribe((event) => {
      if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        reloadOnce(event.url, 'chunk-load-error');
      } else if (event instanceof NavigationStart && this.newReleaseDeployed) {
        reloadOnce(event.url, 'new-release');
      } else if (
        event instanceof NavigationEnd &&
        this.runningRelease &&
        Date.now() - this.lastCheckAt > CHECK_AFTER_NAVIGATION_MILLISECONDS
      ) {
        void this.check();
      }
    });
    this.destroyRef.onDestroy(() => navigation.unsubscribe());

    if (!this.runningRelease) {
      return;
    }

    const checkIfVisible = () => this.checkIfVisible();
    const interval = this.zone.runOutsideAngular(() => {
      this.document.addEventListener('visibilitychange', checkIfVisible);
      return setInterval(checkIfVisible, CHECK_INTERVAL_MILLISECONDS);
    });
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('visibilitychange', checkIfVisible);
      clearInterval(interval);
    });
  }

  private checkIfVisible(): void {
    if (this.document.visibilityState === 'visible') {
      void this.check();
    }
  }

  private async check(): Promise<void> {
    this.lastCheckAt = Date.now();
    try {
      const response = await fetch('version.json', { cache: 'no-store' });
      if (!response.ok) {
        return;
      }
      const deployed: { hash?: unknown } = await response.json();
      if (typeof deployed.hash === 'string' && deployed.hash && deployed.hash !== this.runningRelease) {
        this.newReleaseDeployed = true;
        this.announceNewRelease(deployed.hash);
      }
    } catch {
      // No news; the next check tries again
    }
  }

  /** Replaces the announcement of an earlier release, so that a tab left open over two deployments shows one. */
  private announceNewRelease(hash: string): void {
    const announcement = newReleaseAnnouncement(hash);
    if (this.announcedId && this.announcedId !== announcement.id) {
      this.announcementService.remove(this.announcedId);
    }
    this.announcementService.show(announcement);
    this.announcedId = announcement.id;
  }
}

/** The announcement of the deployed release `hash`, with a button that loads it. */
export function newReleaseAnnouncement(hash: string): Announcement {
  return {
    id: `new-release-${hash}`,
    message: 'release.new_version_available',
    scope: 'release',
    severity: 'info',
    // Not reloadOnce: its loop guard is for reloads nobody asked for, and this one was asked for
    action: { label: 'release.refresh', run: () => pageLoader.load(window.location.href) },
    closable: true,
  };
}
